"use client";

import { Button } from "@/components/ui/Button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  HelpCircle,
  Home,
  LogIn,
  Menu,
  Package,
  Search,
  Settings,
  ShoppingBag,
  Store,
  Tag,
  User,
  UserPlus,
  Bookmark,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  isLogout?: boolean;
}

const mainNavItems: NavItem[] = [
  {
    title: "Home",
    href: "/",
    icon: Home,
    description: "Browse featured products and categories",
  },
  {
    title: "Search",
    href: "/search",
    icon: Search,
    description: "Find specific products",
  },
  {
    title: "Stalls",
    href: "/stalls",
    icon: Store,
    description: "Explore seller stalls",
  },
  {
    title: "Products",
    href: "/products",
    icon: Package,
    description: "Browse all products",
  },
  {
    title: "All categories",
    href: "/categories",
    icon: Tag,
    description: "Browse all categories",
  },
];

const accountNavItems: NavItem[] = [
  {
    title: "My Profile",
    href: "/profile",
    icon: User,
    description: "Manage your account",
  },
  {
    title: "My Orders",
    href: "/orders",
    icon: ShoppingBag,
    description: "View your purchases",
  },
  {
    title: "Favorites",
    href: "/favorites",
    icon: Heart,
    description: "Your favourite items",
  },
  {
    title: "Save for Later",
    href: "/save-for-later",
    icon: Bookmark,
    description: "Items saved for later",
  },
  {
    title: "Settings",
    href: "/settings",
    icon: Settings,
    description: "Account preferences",
  },
];

const supportNavItems: NavItem[] = [
  {
    title: "Help",
    href: "/help",
    icon: HelpCircle,
    description: "Get support",
  }
];

const bottomStickNavItems: NavItem[] = [
  {
    title: "Logout",
    href: "/logout",
    icon: LogIn,
    description: "Sign out of your account",
    isLogout: true,
  }
]

const authNavItems: NavItem[] = [
  {
    title: "Sign In",
    href: "/login",
    icon: LogIn,
    description: "Sign in to your account",
  },
  {
    title: "Sign Up",
    href: "/register",
    icon: UserPlus,
    description: "Create a new account",
  },
];

interface SidebarProps {
  className?: string;
  isAuthenticated?: boolean;
  isCollapsed?: boolean;
}

export function MainSidebar({ className, isAuthenticated = false, isCollapsed = false }: SidebarProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  // Listen for sidebar toggle events from header button
  useEffect(() => {
    const handleSidebarToggle = () => {
      // The parent layout component manages the state, so we don't need to do anything here
      // The parent will re-render this component with the new isCollapsed prop
    };

    window.addEventListener('sidebar-toggle', handleSidebarToggle as EventListener);

    return () => {
      window.removeEventListener('sidebar-toggle', handleSidebarToggle as EventListener);
    };
  }, []);

  const toggleCollapsed = () => {
    // Dispatch event to notify the parent layout component
    window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: !isCollapsed }));
  };

  const SidebarContent = () => (
    <div className={cn(
      "flex flex-col bg-white border-r transition-all duration-300 h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)]",
      isCollapsed ? "w-16" : "w-60"
    )}>
      {/* Header with Collapse Button */}
      <div className={cn(
        "flex items-center border-b px-6 py-4 transition-all duration-300",
        isCollapsed ? "justify-center px-2" : "justify-between"
      )}>
        {!isCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span className="font-bold text-lg">Navigation</span>
          </div>
        )}
        {/* Collapse/Expand Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleCollapsed}
          className={cn(
            "h-8 w-8 transition-all duration-300",
            isCollapsed ? "rotate-180" : ""
          )}
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          <span className="sr-only">
            {isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          </span>
        </Button>
      </div>

      {/* Scrollable Navigation Content */}
      <div className="flex-1 overflow-auto py-6">
        <nav className={cn(
          "space-y-2 transition-all duration-300",
          isCollapsed ? "px-2" : "px-3"
        )}>
          {!isCollapsed && (
            <div className="mb-6">
              <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Browse
              </h3>
            </div>
          )}
          <div className="space-y-1">
            {mainNavItems.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300",
                    isCollapsed ? "justify-center space-x-0 w-12 h-12 mx-auto" : "space-x-3",
                    isActive
                      ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  title={isCollapsed ? item.title : undefined}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.title}</span>}
                </Link>
              );
            })}
          </div>

          {/* Account Section - Only show for authenticated users */}
          {isAuthenticated && (
            <>
              {!isCollapsed && <div className="border-t pt-6" />}
              {!isCollapsed && (
                <div>
                  <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Account
                  </h3>
                </div>
              )}
              <div className="space-y-1">
                {accountNavItems.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300",
                        isCollapsed ? "justify-center space-x-0 w-12 h-12 mx-auto" : "space-x-3",
                        isActive
                          ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      title={isCollapsed ? item.title : undefined}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {/* Authentication Section - Only show for non-authenticated users */}
          {!isAuthenticated && (
            <>
              {!isCollapsed && <div className="border-t pt-6" />}
              {!isCollapsed && (
                <div>
                  <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Account
                  </h3>
                </div>
              )}
              <div className="space-y-1">
                {authNavItems.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300",
                        isCollapsed ? "justify-center space-x-0 w-12 h-12 mx-auto" : "space-x-3",
                        isActive
                          ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      title={isCollapsed ? item.title : undefined}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {!isCollapsed && <div className="border-t pt-6" />}
          {!isCollapsed && (
            <div>
              <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Support
              </h3>
            </div>
          )}
          <div className="space-y-1">
            {supportNavItems.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300",
                    isCollapsed ? "justify-center space-x-0 w-12 h-12 mx-auto" : "space-x-3",
                    isActive
                      ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  title={isCollapsed ? item.title : undefined}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.title}</span>}
                </Link>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Logout Section - Fixed at Bottom (outside scrollable area) */}
      {isAuthenticated && (<div className="border-t p-4 flex-shrink-0">
        <div className="space-y-1">
          {bottomStickNavItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 w-full",
                  isCollapsed ? "justify-center space-x-0 w-12 h-12 mx-auto" : "space-x-3",
                  isActive
                    ? "bg-red-50 text-red-700 border-r-2 border-red-700"
                    : item.isLogout
                      ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                )}
                title={isCollapsed ? item.title : undefined}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span>{item.title}</span>}
              </Link>
            );
          })}
        </div>
      </div>
      )}

      {/* Footer */}
      <div className="border-t p-4 flex-shrink-0">
        <div className="text-xs text-gray-500 text-center">
          © 2025 Retro Stall
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button - Always visible for all users */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="bg-white shadow-md hover:bg-gray-50">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle navigation menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0 h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)]">
            {/* Mobile sidebar always shows expanded content */}
            <div className="flex flex-col bg-white border-r h-full w-60">
              {/* Header with Collapse Button - Hidden on mobile */}
              <div className="flex items-center justify-between border-b px-6 py-4">
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-8 rounded bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">RS</span>
                  </div>
                  <span className="font-bold text-lg">Navigation</span>
                </div>
              </div>

              {/* Scrollable Navigation Content - Always expanded on mobile */}
              <div className="flex-1 overflow-auto py-6">
                <nav className="space-y-2 px-3">
                  <div className="mb-6">
                    <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Browse
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {mainNavItems.map((item) => {
                      const isActive = pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "flex items-center rounded-lg px-3 py-2 text-sm font-medium space-x-3",
                            isActive
                              ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          )}
                        >
                          <item.icon className="h-5 w-5 flex-shrink-0" />
                          <span>{item.title}</span>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Account Section - Only show for authenticated users */}
                  {isAuthenticated && (
                    <>
                      <div className="border-t pt-6" />
                      <div>
                        <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Account
                        </h3>
                      </div>
                      <div className="space-y-1">
                        {accountNavItems.map((item) => {
                          const isActive = pathname === item.href;
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => setIsOpen(false)}
                              className={cn(
                                "flex items-center rounded-lg px-3 py-2 text-sm font-medium space-x-3",
                                isActive
                                  ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                              )}
                            >
                              <item.icon className="h-5 w-5 flex-shrink-0" />
                              <span>{item.title}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Authentication Section - Only show for non-authenticated users */}
                  {!isAuthenticated && (
                    <>
                      <div className="border-t pt-6" />
                      <div>
                        <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Account
                        </h3>
                      </div>
                      <div className="space-y-1">
                        {authNavItems.map((item) => {
                          const isActive = pathname === item.href;
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => setIsOpen(false)}
                              className={cn(
                                "flex items-center rounded-lg px-3 py-2 text-sm font-medium space-x-3",
                                isActive
                                  ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                              )}
                            >
                              <item.icon className="h-5 w-5 flex-shrink-0" />
                              <span>{item.title}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div className="border-t pt-6" />
                  <div>
                    <h3 className="mb-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Support
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {supportNavItems.map((item) => {
                      const isActive = pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "flex items-center rounded-lg px-3 py-2 text-sm font-medium space-x-3",
                            isActive
                              ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          )}
                        >
                          <item.icon className="h-5 w-5 flex-shrink-0" />
                          <span>{item.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </nav>
              </div>

              {/* Logout Section - Fixed at Bottom */}
              {isAuthenticated && (
                <div className="border-t p-4 flex-shrink-0">
                  <div className="space-y-1">
                    {bottomStickNavItems.map((item) => {
                      const isActive = pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "flex items-center rounded-lg px-3 py-2 text-sm font-medium w-full space-x-3",
                            isActive
                              ? "bg-red-50 text-red-700 border-r-2 border-red-700"
                              : item.isLogout
                                ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          )}
                        >
                          <item.icon className="h-5 w-5 flex-shrink-0" />
                          <span>{item.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="border-t p-4 flex-shrink-0">
                <div className="text-xs text-gray-500 text-center">
                  © 2025 Retro Stall
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar - Fixed Position */}
      <div className={cn("hidden lg:fixed lg:top-8 lg:left-0 lg:z-30 lg:flex lg:flex-col", className)}>
        <SidebarContent />
      </div>
    </>
  );
}
